import { useCallback, useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import {
  GetGlAccountOptionsDocument,
  type GlAccount,
  type GlAccountClassification,
  type GlAccountQueryListArgs,
  type GlAccountSubType,
  type GlAccountType,
  type Query,
  ReportingAccountType,
} from "@/graphql/codegen/graphql"

interface GlAccountOption {
  id: string
  label: string
  metadata?: {
    type?: GlAccountType | null
    subType?: GlAccountSubType | null
    classification?: GlAccountClassification | null
  }
}

interface UseGlAccountOptionsReturn {
  glAccounts: GlAccountOption[]
  isFetching: boolean
  error?: CombinedError
  setSearchTerm: (term: string) => void
}

const LIMIT = 20

const transformToOption = (glAccount: GlAccount): GlAccountOption => {
  const accountNumber = glAccount.subNumber ? `${glAccount.number}-${glAccount.subNumber}` : glAccount.number

  return {
    id: glAccount.glAccountId ?? "",
    label: `${accountNumber} - ${glAccount.title}`,
    metadata: {
      type: glAccount.type,
      subType: glAccount.subType,
      classification: glAccount.classification,
    },
  }
}

export const useGlAccountOptions = ({ pause }: { pause?: boolean }): UseGlAccountOptionsReturn => {
  const [glAccounts, setGlAccounts] = useState<GlAccountOption[]>([])
  const [searchTerm, setSearchTerm] = useState("")

  const [{ data, fetching, error }] = useQuery<Pick<Query, "glAccount">, GlAccountQueryListArgs>({
    query: GetGlAccountOptionsDocument,
    variables: {
      input: {
        filter: {
          reportingAccountType: ReportingAccountType.Parent,
          isActive: true,
          query: searchTerm || undefined,
        },
        offset: 0,
        limit: LIMIT,
      },
    },
    pause,
  })

  useEffect(() => {
    if (data?.glAccount.list.__typename !== "GlAccountListSuccess") return

    const accounts = data.glAccount.list.glAccounts.filter((glAccount) => glAccount !== null).map(transformToOption)

    accounts.sort((a, b) => a.label.localeCompare(b.label))
    setGlAccounts(accounts)
  }, [data])

  const handleSetSearchTerm = useCallback((term: string) => {
    setSearchTerm(term)
  }, [])

  return {
    glAccounts,
    isFetching: fetching,
    error,
    setSearchTerm: handleSetSearchTerm,
  }
}
